// @ts-ignore
import VAR from './variabels.module.scss';
import { alpha, ThemeOptions } from '@mui/material/styles';
import { drawerClasses } from '@mui/material/Drawer';
import { inputBaseClasses } from '@mui/material/InputBase';
import { outlinedInputClasses } from '@mui/material/OutlinedInput';

/**
 * MUI theme options (Phase D1: MUI 4 -> 5). The provider in
 * contexts/theme-provider creates the two directional themes from this object.
 *
 * Breakpoints come from the SCSS map (`_variabels.scss` -> `:export` in
 * `variabels.module.scss`), so `map-get($breakpoints, 'md')` and
 * `theme.breakpoints.down('md')` are the same edge (rules/responsive.md).
 *
 * v4 -> v5 defaults that changed and are pinned back here so the screens stay
 * pixel-identical (docs/review/d1/wrapper-inventory.md, section 3.1):
 *  - Button default color: v4 `default` (grey) -> v5 `primary`; `inherit` in v5
 *    renders v4's `default` for text and contained buttons.
 *  - TextField / Select / FormControl default variant: `standard` -> `outlined`.
 *  - Link default underline: `hover` -> `always`.
 *  - IconButton size small: 3px padding in v4, 5px in v5 (the root override
 *    below would otherwise win over the size style; v4 let sizeSmall win).
 *  - CssBaseline body typography: v4 spread `body2`, v5 spreads `body1`.
 *  - breakpoints.down(key) is inclusive of `key` in v5: v4 `down('xs')` is
 *    v5 `down('sm')` (both `max-width: 575.95px` on this map).
 *  - Palette: v5 moved error/warning/info/success to darker mains,
 *    text.secondary from 0.54 to 0.6 alpha and background.default to #fff;
 *    the v4 values are restated.
 *  - Input metrics: v5 InputBase is `padding: 4px 0 5px; height: 1.4375em`
 *    (v4 `6px 0 7px; 1.1876em`), InputLabel rests at 20px (v4 24px),
 *    OutlinedInput input padding 16.5px (v4 18.5px), FormLabel line-height
 *    1.4375em (v4 1); every form row moves by a few px without these.
 *  - Checkbox / Radio / Switch default color `secondary` -> `primary`,
 *    Tabs indicatorColor `secondary` -> `primary` and textColor
 *    `inherit` -> `primary`, ButtonGroup color `default` -> `primary`.
 *  - DialogTitle renders its text directly (v4 wrapped it in an h6
 *    Typography, which capitalized it).
 *  - Slider: v5 doubled the rail (4px) and thumb (20px), added a track
 *    border and moved mark labels to 30px.
 *  - Tab: v4 minWidth 72 / 160 above sm and maxWidth 264 (v5: 90 / 360).
 *  - Selected states: v5 tints MenuItem / ListItem / ListItemButton / TableRow
 *    with `primary` at selectedOpacity (v4: `action.selected` = 8 % black;
 *    TableRow: secondary) and ToggleButton at 8 % (v4: 12 %).
 */

const FontSize = '1.4rem';

export const appBreakpoints = {
  values: {
    xs: Number(VAR.xs),
    sm: Number(VAR.sm),
    md: Number(VAR.md),
    lg: Number(VAR.lg),
    xl: Number(VAR.xl),
    xxl: Number(VAR.xxl),
  },
};

const HStyle = {
  color: VAR.colorSecondary,
  // textTransform: 'uppercase' as const
  textTransform: 'capitalize' as const,
};

/**
 * MUI 9 positions the badge with inline CSS variables (`--Badge-inset`,
 * `--Badge-translate`, `--Badge-origin`) that stylis-plugin-rtl never sees, so
 * under RTL the badge stays on the physical right where MUI 4 (jss-rtl) mirrored
 * `right: 0` to the left. The mirrored geometry per anchor / overlap class,
 * `@noflip`-marked so the RTL cache leaves the values alone (cssjanus would
 * negate the translate again).
 */
const rtlBadgeGeometry = (() => {
  const noflip = (value: string) => `${value} /*! @noflip */`;
  const rules: Record<string, Record<string, unknown>> = {};
  (['Top', 'Bottom'] as const).forEach((vertical) => {
    (['Right', 'Left'] as const).forEach((horizontal) => {
      (['Rectangular', 'Circular'] as const).forEach((overlap) => {
        const offset = overlap === 'Circular' ? '14%' : '0';
        const mirroredToLeft = horizontal === 'Right';
        const top = vertical === 'Top' ? offset : 'auto';
        const bottom = vertical === 'Bottom' ? offset : 'auto';
        const left = mirroredToLeft ? offset : 'auto';
        const right = mirroredToLeft ? 'auto' : offset;
        const translate = `${mirroredToLeft ? '-50%' : '50%'}, ${
          vertical === 'Top' ? '-50%' : '50%'
        }`;
        const origin = `${mirroredToLeft ? '0%' : '100%'} ${vertical === 'Top' ? '0%' : '100%'}`;
        rules[`&.MuiBadge-anchorOrigin${vertical}${horizontal}${overlap}`] = {
          inset: noflip(`${top} ${right} ${bottom} ${left}`),
          transform: noflip(`scale(1) translate(${translate})`),
          transformOrigin: noflip(origin),
          '&.MuiBadge-invisible': { transform: noflip(`scale(0) translate(${translate})`) },
        };
      });
    });
  });
  return rules;
})();

const themeOptions: ThemeOptions = {
  breakpoints: appBreakpoints,
  typography: {
    fontSize: 14,
    htmlFontSize: 10,
    fontWeightRegular: 400,
    fontFamily: 'Arial, Geneva, Helvetica, sans-serif;',
    h1: {
      ...HStyle,
      fontSize: '2.4rem',
    },
    h2: {
      ...HStyle,
      fontSize: '2rem',
    },
    h3: {
      ...HStyle,
      fontSize: '1.8rem',
    },
    h4: {
      ...HStyle,
      fontSize: '1.6rem',
      fontWeight: 'bold',
    },
    h5: {
      ...HStyle,
      fontSize: '1.6rem',
      textTransform: 'capitalize',
    },
    h6: {
      ...HStyle,
      fontSize: '1.4rem',
      textTransform: 'capitalize',
    },
    body1: {
      fontSize: '1.4rem',
    },
    body2: {
      fontSize: '1.2rem',
    },
  },
  palette: {
    text: {
      primary: 'rgba(0, 0, 0, 0.87);',
      secondary: 'rgba(0, 0, 0, 0.54)',
      disabled: 'rgba(0, 0, 0, 0.38)',
    },
    background: {
      default: '#fafafa',
    },
    primary: {
      main: VAR.colorPrimary,
      contrastText: '#fff',
    },
    secondary: {
      main: VAR.colorSecondary,
      contrastText: '#fff',
    },
    // MUI 4 defaults, restated: v5 darkened the mains (error #d32f2f, warning #ed6c02, ...).
    error: { light: '#e57373', main: '#f44336', dark: '#d32f2f', contrastText: '#fff' },
    warning: {
      light: '#ffb74d',
      main: '#ff9800',
      dark: '#f57c00',
      contrastText: 'rgba(0, 0, 0, 0.87)',
    },
    info: { light: '#64b5f6', main: '#2196f3', dark: '#1976d2', contrastText: '#fff' },
    success: {
      light: '#81c784',
      main: '#4caf50',
      dark: '#388e3c',
      contrastText: 'rgba(0, 0, 0, 0.87)',
    },
  },
  components: {
    // Phase F1 (Task 2): responsive headings below md only. Added as a
    // Typography variant override (not in `typography.h1`) so the desktop
    // value above is untouched and the edge is `theme.breakpoints`, never a
    // pixel literal (rules/responsive.md).
    MuiTypography: {
      styleOverrides: {
        h1: ({ theme }) => ({
          [theme.breakpoints.down('md')]: { fontSize: '2rem' },
        }),
        h2: ({ theme }) => ({
          [theme.breakpoints.down('md')]: { fontSize: '1.8rem' },
        }),
      },
    },
    MuiCssBaseline: {
      // v4 CssBaseline applied typography.body2 to <body>; v5 applies body1.
      styleOverrides: (theme) => ({
        body: {
          ...theme.typography.body2,
        },
      }),
    },
    MuiTabs: {
      defaultProps: {
        indicatorColor: 'secondary',
        textColor: 'inherit',
      },
      styleOverrides: {
        indicator: {
          top: '0',
          bottom: 'auto',
          height: '3px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontWeight: 'bold',
          textTransform: 'inherit',
          fontSize: '1.6rem',
          background: '#F6F8F8',
          padding: '0.5rem 2.5rem',
          lineHeight: '1.1',
          // MUI 4 tab widths (v5: minWidth 90, maxWidth 360)
          minWidth: 72,
          maxWidth: 264,
          [theme.breakpoints.up('sm')]: {
            minWidth: 160,
          },
          '&.Mui-selected': {
            background: '#fff',
          },
        }),
        textColorPrimary: {
          color: VAR.colorGreyDark,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        // MUI 9 has no paperAnchorDocked* paper classes: the docked root carries the anchor
        docked: {
          [`&.${drawerClasses.anchorLeft} .${drawerClasses.paper}`]: {
            borderColor: VAR.colorBorder,
            borderWidth: 2,
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
        color: 'inherit',
      },
      styleOverrides: {
        root: {
          fontSize: '1.4rem',
          borderRadius: '0.4rem',
          textTransform: 'lowercase',
          lineHeight: 1.2,
          minHeight: '3.65rem',
        },
        sizeSmall: {
          padding: '0.4rem 1rem',
          lineHeight: '1.2',
          minWidth: '2rem',
          fontWeight: 'bold',
          textTransform: 'uppercase',
          fontSize: '1.1rem',
          minHeight: 'auto',
        },
      },
    },
    MuiButtonGroup: {
      defaultProps: {
        disableElevation: true,
        color: 'inherit',
      },
    },
    MuiCheckbox: {
      defaultProps: {
        color: 'secondary',
      },
    },
    MuiRadio: {
      defaultProps: {
        color: 'secondary',
      },
    },
    MuiSwitch: {
      defaultProps: {
        color: 'secondary',
      },
    },
    MuiLink: {
      defaultProps: {
        underline: 'hover',
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: ({ theme }) => (theme.direction === 'rtl' ? rtlBadgeGeometry : {}),
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'standard',
      },
    },
    MuiFormControl: {
      defaultProps: {
        variant: 'standard',
      },
      styleOverrides: {
        root: {
          margin: '0 0 1.5rem',
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          minHeight: 'inherit',
          // MUI 4 selected state: action.selected (v5 tints with primary, invisible on the blue nav)
          '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
          },
        },
      },
    },
    MuiListItemIcon: {
      // MUI 4..8 metric (MUI 9: 36px)
      styleOverrides: {
        root: {
          minWidth: 56,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          // MUI 4: selected = 12 % of text.primary (v5: 8 %)
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 0, 0, 0.12)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.15)',
            },
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) => ({
          // MUI 4 selected row: secondary at selectedOpacity (v5: primary)
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: alpha(
              theme.palette.secondary.main,
              theme.palette.action.selectedOpacity,
            ),
          },
        }),
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          padding: '0.7rem',
        },
        sizeSmall: {
          padding: 3,
        },
      },
    },
    // GRID (MUI 7: Grid v2, CSS gap). The MUI 4 theme killed the vertical spacing on
    // every grid (item paddingTop/Bottom 0, container marginTop/Bottom 0) and kept only
    // the horizontal half-spacing on each side; the same layout in gap terms is a
    // column gap of 8 * spacing and no row gap. The old `spacing={4}` special case
    // (10px per side = 20px) is written at its call sites as `columnSpacing={2.5}`.
    // The legacy container was `width: calc(100% + spacing)`; Grid v2 leaves the width
    // to the parent, which shrink-wraps it inside the flex toolbars.
    MuiGrid: {
      styleOverrides: {
        root: {
          '&.MuiGrid-container': {
            rowGap: 0,
            width: '100%',
          },
        },
      },
    },
    // DIALOG
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          textTransform: 'capitalize',
          padding: ' 1.5rem 2rem',
          backgroundColor: VAR.colorGreyLight,
          display: 'flex',
          [theme.breakpoints.down('sm')]: {
            padding: ' 1rem 1.6rem',
          },
          // N1 (brief pattern E): below md a dialog is a full screen (D-54) and
          // takes the screen's gutter, `$mobile-gutter` (styles/_mobile.scss)
          [theme.breakpoints.down('md')]: {
            paddingLeft: '0.8rem',
            paddingRight: '0.8rem',
          },
        }),
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: '2rem',
          [theme.breakpoints.down('sm')]: {
            padding: ' 1rem 1.6rem',
          },
          // N1 (brief pattern E): below md a dialog is a full screen (D-54) and
          // takes the screen's gutter, `$mobile-gutter` (styles/_mobile.scss)
          [theme.breakpoints.down('md')]: {
            paddingLeft: '0.8rem',
            paddingRight: '0.8rem',
          },
        }),
      },
    },
    MuiDialog: {
      styleOverrides: {
        // Phase F1 (Task 5a, D-54): every dialog is full-screen below md. 145
        // sites render the raw MUI Dialog and there is no shared wrapper, so the
        // theme is the one place; `fullScreen` itself is a boolean prop with no
        // responsive form, hence the paper geometry MUI's own `paperFullScreen`
        // sets, restated under the theme's `md` edge. Nested under `root` so it
        // outranks any single-class paper rule (x-date-pickers' modal paper).
        root: ({ theme }) => ({
          [theme.breakpoints.down('md')]: {
            '& .MuiDialog-container': { alignItems: 'stretch' },
            '& .MuiDialog-paper': {
              margin: 0,
              width: '100%',
              maxWidth: '100%',
              height: '100%',
              maxHeight: 'none',
              borderRadius: 0,
            },
          },
        }),
        paper: {
          overflowY: 'inherit',
          borderRadius: '0.8rem',
          overflow: 'hidden',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: '2rem',
          [theme.breakpoints.down('sm')]: {
            padding: ' 1rem 1.6rem',
          },
          // N1 (brief pattern E): below md a dialog is a full screen (D-54) and
          // takes the screen's gutter, `$mobile-gutter` (styles/_mobile.scss)
          [theme.breakpoints.down('md')]: {
            paddingLeft: '0.8rem',
            paddingRight: '0.8rem',
          },
        }),
      },
    },

    MuiInputBase: {
      // MUI 4 metrics (v5: root 1.4375em, input 4px 0 5px / 1.4375em, small paddingTop 1, multiline 4px 0 5px)
      styleOverrides: {
        root: {
          lineHeight: '1.1876em',
        },
        input: {
          height: '1.1876em',
          padding: '6px 0 7px',
        },
        // MUI 9 dropped the composed input classes (inputSizeSmall, inputMultiline):
        // the root state class scopes the input rule instead
        sizeSmall: {
          [`& .${inputBaseClasses.input}`]: {
            paddingTop: 3,
          },
        },
        multiline: {
          padding: '6px 0 7px',
          [`& .${inputBaseClasses.input}`]: {
            height: 'auto',
            padding: 0,
          },
        },
      },
    },
    MuiOutlinedInput: {
      // MUI 4 metrics (v5: input 16.5px 14px, small 8.5px 14px, multiline root 16.5px 14px)
      styleOverrides: {
        input: {
          padding: '18.5px 14px',
        },
        sizeSmall: {
          [`& .${outlinedInputClasses.input}`]: {
            paddingTop: 10.5,
            paddingBottom: 10.5,
          },
        },
        multiline: {
          padding: '18.5px 14px',
          [`& .${outlinedInputClasses.input}`]: {
            padding: 0,
          },
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        variant: 'standard',
      },
      styleOverrides: {
        select: {
          minHeight: '1.1876em',
        },
      },
    },
    MuiSlider: {
      // MUI 4 geometry (v5: 4px rail, 20px thumb, bordered track, mark labels at 30px)
      styleOverrides: {
        root: {
          height: 2,
          padding: '13px 0',
        },
        thumb: {
          width: 12,
          height: 12,
        },
        track: {
          border: 'none',
        },
        markLabel: {
          top: 26,
        },
      },
    },
    MuiAutocomplete: {
      // MUI 4 input paddings inside the Autocomplete (v5: standard 4px 4px 4px 0 with no
      // first-child rule, outlined 7.5px 4px 7.5px 5px, small 2px 4px 3px 0 / 2.5px 4px 2.5px 8px)
      styleOverrides: {
        inputRoot: {
          '&.MuiInput-root': {
            '& .MuiAutocomplete-input': {
              padding: 4,
            },
            '& .MuiAutocomplete-input:first-child': {
              padding: '6px 0',
            },
          },
          '&.MuiInput-root.MuiInputBase-sizeSmall': {
            '& .MuiAutocomplete-input': {
              padding: '4px 4px 5px',
            },
            '& .MuiAutocomplete-input:first-child': {
              padding: '3px 0 6px',
            },
          },
          '&.MuiOutlinedInput-root': {
            '& .MuiAutocomplete-input': {
              padding: '9.5px 4px',
            },
            '& .MuiAutocomplete-input:first-child': {
              paddingLeft: 6,
            },
          },
          '&.MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
            '& .MuiAutocomplete-input': {
              padding: '4.5px 4px',
            },
            // the first-child start padding of the outlined rule above, restated after the
            // small rule so it survives the cascade (MUI 9: the grid page-size select moved 2 px)
            '& .MuiAutocomplete-input:first-child': {
              paddingLeft: 6,
            },
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        // MUI 4 resting positions (v5: 20px / 17px)
        formControl: {
          transform: 'translate(0, 24px) scale(1)',
        },
        sizeSmall: {
          transform: 'translate(0, 21px) scale(1)',
        },
        shrink: {
          transform: 'translate(0, .4rem)',
          fontSize: '1.2rem',
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: FontSize,
          lineHeight: 1,
          color: '#888D8F',
          '&.Mui-focused': {
            color: '#888D8F',
          },
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        positionStart: {
          color: VAR.colorBorder,
        },
      },
    },
    MuiInput: {
      styleOverrides: {
        root: {
          fontSize: FontSize,
        },
        underline: {
          '&:before': {
            borderBottomColor: VAR.colorBorder,
          },
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        message: {
          fontSize: FontSize,
        },
      },
    },
    // MuiSvgIcon: {
    //   root: {
    //     'html[dir=rtl] &': {
    //       transform: 'scaleX(-1)',
    //     },
    //   },
    // },
  },
};

export default themeOptions;
